import { useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  PenTool,
  LayoutDashboard,
  Users,
  Upload,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  School,
  BookOpen,
  HelpCircle,
  Building2,
  ShieldCheck,
  ClipboardList,
  TrendingUp,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPrimaryRoleLabel } from '@/lib/roles';
import BillingStatusBanner from '@/components/BillingStatusBanner';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  schoolPlans?: ('school' | 'individual')[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Trust Dashboard', href: '/trust-dashboard', icon: Building2, roles: ['trust_leader'] },
  { label: 'Schools', href: '/admin/schools', icon: ShieldCheck, roles: ['super_admin'] },
  { label: 'Users', href: '/admin/users', icon: Users, roles: ['super_admin'] },
  { label: 'Assessments (admin)', href: '/admin/assessments', icon: FileText, roles: ['super_admin'] },
  { label: 'Audit Log', href: '/admin/audit-log', icon: ClipboardList, roles: ['super_admin'] },
  { label: 'Analytics', href: '/admin/analytics', icon: TrendingUp, roles: ['super_admin'] },
  { label: 'Leads', href: '/admin/leads', icon: Inbox, roles: ['super_admin'] },
  { label: 'Pupils', href: '/pupils', icon: Users, roles: ['school_admin', 'slt', 'department_leader', 'teacher'] },
  { label: 'Upload Writing', href: '/upload', icon: Upload, roles: ['school_admin', 'department_leader', 'teacher'] },
  { label: 'Assessments', href: '/assessments', icon: FileText },
  { label: 'Progress', href: '/progress', icon: BarChart3 },
  { label: 'My Classes', href: '/my-classes', icon: BookOpen, schoolPlans: ['individual'] },
  { label: 'School Setup', href: '/school-setup', icon: School, roles: ['school_admin'], schoolPlans: ['school'] },
  { label: 'Settings', href: '/settings', icon: Settings },
];

interface DashboardLayoutProps {
  children: ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, roles, signOut, hasRole, schoolPlan, billingStatus, trialEndsAt, planTier, suspendedAt } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const getInitials = (name?: string | null) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredNavItems = navItems.filter((item) => {
    // Super admin: only show their admin section + Dashboard + Settings
    if (hasRole('super_admin')) {
      const superAdminAllowed = ['/dashboard', '/settings'];
      return item.roles?.includes('super_admin') || superAdminAllowed.includes(item.href);
    }
    if (item.roles && !item.roles.some((r) => hasRole(r as any))) return false;
    if (item.schoolPlans && item.schoolPlans.length > 0) {
      if (schoolPlan === null) return false; // hide until plan is resolved
      if (!item.schoolPlans.includes(schoolPlan)) return false;
    }
    return true;
  });

  const NavContent = () => (
    <nav className="flex flex-col gap-1 p-2">
      {filteredNavItems.map((item) => {
        const isActive = location.pathname === item.href;
        const Icon = item.icon;
        
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => setIsMobileNavOpen(false)}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            )}
          >
            <Icon className="h-5 w-5" />
            {item.label}
            {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 border-r border-sidebar-border bg-sidebar">
        <div className="flex h-16 items-center gap-2 px-4 border-b border-sidebar-border">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg text-sidebar-foreground">WriteAssess</span>
          </Link>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4">
          <NavContent />
        </div>

        <div className="px-2 pb-1 border-t border-sidebar-border pt-2">
          <a
            href="/blog"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
          >
            <BookOpen className="h-4 w-4" />
            Blog
          </a>
          <Link
            to="/help"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
          >
            <HelpCircle className="h-4 w-4" />
            Help Centre
          </Link>
        </div>

        <div className="p-4 border-t border-sidebar-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {getInitials(profile?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">
                    {profile?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {schoolPlan === 'individual' ? 'Teacher' : getPrimaryRoleLabel((roles[0]?.role as any) ?? null)}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
          <Sheet open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex h-16 items-center gap-2 px-4 border-b">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
                    <PenTool className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="font-bold text-lg">WriteAssess</span>
                </Link>
              </div>
              <div className="py-4">
                <NavContent />
                <div className="px-2 pt-1 border-t mt-2">
                  <Link
                    to="/help"
                    onClick={() => setIsMobileNavOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
                  >
                    <HelpCircle className="h-5 w-5" />
                    Help Centre
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-bold">WriteAssess</span>
          </Link>

          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/help" aria-label="Help Centre">
                <HelpCircle className="h-5 w-5 text-muted-foreground" />
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {getInitials(profile?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-medium">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">
                    {schoolPlan === 'individual' ? 'Teacher' : getPrimaryRoleLabel((roles[0]?.role as any) ?? null)}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <BillingStatusBanner
          billingStatus={billingStatus}
          trialEndsAt={trialEndsAt}
          planTier={planTier}
          suspendedAt={suspendedAt}
        />

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
